import './OrderPage.scss';
import { useTranslation } from 'react-i18next';
import React, { useRef, useState } from 'react';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { PersonalDetailsStep } from './steps/PersonalDetailsStep';
import { OrderPersonalDetailsRequestData, WaitingListService } from '../api';
import { useLanguage } from '../utils/GeneralUtils';
import './WaitingList.scss';
import { OrderPageButton } from './themedComponents/OrderPageButton';
import { OrderPageTypography } from './themedComponents/OrderPageTypography';
import MonkeybookWidgetFooter from '../MonkeybookWidgetFooter';
import { getOrderPage } from '../utils/AccountUtils';
import { useAccountData } from './components/AccountSpecificThemeWrapper';
import RotatingLoader from '../ui/RotatingLoader';

const WaitingList = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef();
  const language = useLanguage();
  const { accountId, experienceId, orderPageId, date, persons } = useParams();
  const account = useAccountData();
  const orderPage = getOrderPage(account, orderPageId || '');
  const [loading, setLoading] = useState(false);

  if (!account || !orderPage) {
    return <RotatingLoader />;
  }

  const sendRegisterData = async (values: OrderPersonalDetailsRequestData) => {
    return WaitingListService.registerToWaitingList({
      account_id: accountId!,
      experience_id: experienceId!,
      order_page_id: orderPageId!,
      language,
      persons: +persons!,
      date,
      personal_details: { ...values },
    });
  };

  const submit = async () => {
    const data = formRef;
    // @ts-ignore
    await data?.current.submitForm();
    // @ts-ignore
    const { isValid } = data.current;
    if (isValid) {
      setLoading(true);
      // @ts-ignore
      await sendRegisterData(data.current.values);
      navigate('/waiting-list/completed');
    }
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>
            Monkeybook - {orderPage.label[language]} - {t('order_page.waiting_list.title')}
          </title>
        </Helmet>
      </HelmetProvider>
      <Grid
        container
        display="flex"
        justifyContent="space-around"
        sx={{ '::-webkit-scrollbar': { display: 'none' } }}
        style={{
          height: '100%',
          width: '100%',
        }}>
        <Grid
          container
          alignContent="center"
          flexDirection="column"
          style={{
            borderRadius: '12px',
            background: theme.customTheme.palette.background,
            height: '100%',
            width: '100%',
            maxWidth: '710px',
          }}>
          <Grid
            p={isSmallScreen ? 2 : 4}
            container
            flexDirection="column"
            style={{
              maxWidth: '650px',
              color: theme.customTheme.palette.text.primary,
              border: `2px solid ${theme.customTheme.palette.border}`,
              borderRadius: '12px',
            }}
            boxShadow={4}>
            <Grid item display="flex" justifyContent="center">
              <OrderPageTypography variant="h3" bold>
                {t('order_page.waiting_list.title')}
              </OrderPageTypography>
            </Grid>

            <PersonalDetailsStep formRef={formRef} orderPage={orderPage} />

            <Grid mt={4}>
              <OrderPageButton
                loadingButton
                variant="contained"
                onClick={submit}
                loading={loading}
                size="large"
                className="next-button">
                <span>{t('finish')}</span>
              </OrderPageButton>
            </Grid>
          </Grid>
          <Grid item mt={1}>
            <MonkeybookWidgetFooter languages={account.customer_facing_widgets_languages} />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default WaitingList;
